
import React from 'react';
import { TrendingUp, TrendingDown, Target, Clock, Wifi, WifiOff } from 'lucide-react';

interface PredictionMetricsProps {
  metal: 'ORO' | 'ARGENTO';
  currentPrice: number;
  targetPrice: number;
  targetPercentage: number;
  timeframe: string;
  confidence: number;
  isRealTime?: boolean;
  lastUpdated?: string;
  isLoading?: boolean;
}

export function PredictionMetrics({ 
  metal, 
  currentPrice, 
  targetPrice, 
  targetPercentage, 
  timeframe, 
  confidence,
  isRealTime = false,
  lastUpdated,
  isLoading = false
}: PredictionMetricsProps) {
  const isPositive = targetPercentage > 0;
  const metalColor = metal === 'ORO' ? 'text-gold' : 'text-silver';
  const bgGradient = metal === 'ORO' 
    ? 'bg-gradient-to-br from-gold/5 via-gold/3 to-transparent' 
    : 'bg-gradient-to-br from-silver/5 via-silver/3 to-transparent';
  const borderColor = metal === 'ORO' ? 'border-gold/30' : 'border-silver/30';

  return (
    <div className={`p-6 rounded-xl border-2 ${bgGradient} ${borderColor} shadow-lg`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${metal === 'ORO' ? 'bg-gold/10' : 'bg-silver/10'}`}>
            <Target className={`h-6 w-6 ${metalColor}`} />
          </div>
          <div>
            <h3 className={`text-xl font-bold ${metalColor}`}>
              Previsione {metal}
            </h3>
            <p className="text-sm text-muted-foreground">{timeframe}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-muted-foreground mb-1">Affidabilità</div>
          <div className={`text-lg font-bold ${metalColor}`}>{confidence}%</div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-2">
            <span>Prezzo Attuale</span>
            {isLoading ? (
              <div className="animate-spin h-3 w-3 border border-muted-foreground border-t-transparent rounded-full" />
            ) : isRealTime ? (
              <Wifi className="h-3 w-3 text-green-500" />
            ) : (
              <WifiOff className="h-3 w-3 text-muted-foreground" />
            )}
          </div>
          <div className="text-2xl font-bold text-foreground">
            €{currentPrice.toFixed(2)}
          </div>
          <div className="text-sm text-muted-foreground">
            per grammo
            {isRealTime && lastUpdated && (
              <div className="flex items-center justify-center gap-1 mt-1 text-xs">
                <Clock className="h-3 w-3" />
                <span>Aggiornato: {lastUpdated}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-sm text-muted-foreground mb-2">Target Previsto</div>
          <div className={`text-3xl font-black ${metalColor}`}>
            €{targetPrice.toFixed(2)}
          </div>
          <div className="text-sm text-muted-foreground">per grammo</div>
        </div>
        
        <div className="text-center">
          <div className="text-sm text-muted-foreground mb-2">Variazione</div>
          <div className={`text-2xl font-bold flex items-center justify-center gap-2 ${
            isPositive ? 'text-green-600' : 'text-red-600'
          }`}>
            {isPositive ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
            <span>{isPositive ? '+' : ''}{targetPercentage.toFixed(1)}%</span>
          </div>
          <div className="text-sm text-muted-foreground">variazione attesa</div>
        </div>
      </div>
    </div>
  );
}
